const KIND_ALIASES = {
  assessment: 'assessment',
  assessments: 'assessment',
  report: 'assessment',
  reports: 'assessment',
  'assessment-template': 'assessment-template',
  assessment_template: 'assessment-template',
  assessmentTemplate: 'assessment-template',
  assessmenttemplate: 'assessment-template',
  task: 'task',
  tasks: 'task',
  edition: 'edition',
  editions: 'edition',
  library: 'edition',
  course: 'course',
  courses: 'course',
  document: 'document',
  documents: 'document',
  'library-document': 'document',
  library_document: 'document',
  courseDocument: 'course-document',
  coursedocument: 'course-document',
  'course-document': 'course-document',
  course_document: 'course-document',
};

export const AGENT_RESULT_KINDS = ['assessment', 'assessment-template', 'task', 'edition', 'course', 'document', 'course-document'];

const cleanValue = (value) => {
  if (value === undefined || value === null) return '';
  return `${value}`.trim();
};

const getNested = (obj, path) => path.split('.').reduce((current, key) => (current && current[key] !== undefined ? current[key] : undefined), obj);

const firstValue = (obj, paths) => {
  for (const path of paths) {
    const value = getNested(obj, path);
    if (value !== undefined && value !== null && value !== '') return value;
  }
  return undefined;
};

export const normalizeAgentResultKind = (kind) => {
  const value = cleanValue(kind);
  return KIND_ALIASES[value] || KIND_ALIASES[value.toLowerCase()] || '';
};

export const getAgentResultPath = (result = {}) => {
  const kind = normalizeAgentResultKind(result.kind || result.type || result.component || result.resourceType || result.resource_type);
  const id = cleanValue(firstValue(result, ['id', 'resourceId', 'data.id']));
  const editionId = cleanValue(firstValue(result, ['editionId', 'edition_id', 'data.editionId', 'data.edition_id']));
  const classificationId = cleanValue(firstValue(result, ['classificationId', 'classification_id', 'data.classificationId', 'data.classification_id']));
  const courseId = cleanValue(firstValue(result, ['courseId', 'course_id', 'data.courseId', 'data.course_id']));
  const path = cleanValue(result.path || result.route);

  if (path.startsWith('/') && !path.startsWith('//')) return path;

  switch (kind) {
    case 'assessment':
      return id ? `/reports/assessment/${encodeURIComponent(id)}` : '';
    case 'assessment-template':
      return id ? `/reports/assessment-template/${encodeURIComponent(id)}` : '';
    case 'task':
      return id ? `/tasks/detail/${encodeURIComponent(id)}` : '';
    case 'edition':
      return id ? `/library/${encodeURIComponent(id)}` : '';
    case 'course':
      return id ? `/courses/${encodeURIComponent(id)}` : '';
    case 'course-document':
      return courseId && id ? `/courses/${encodeURIComponent(courseId)}/document/${encodeURIComponent(id)}` : '';
    case 'document':
      if (courseId && id) return `/courses/${encodeURIComponent(courseId)}/document/${encodeURIComponent(id)}`;
      if (editionId && classificationId) return `/library/${encodeURIComponent(editionId)}/document/${encodeURIComponent(classificationId)}`;
      return id ? `/library/${encodeURIComponent(id)}` : '';
    default:
      return '';
  }
};

export const getAgentResultUrl = (result = {}, baseUrl = '') => {
  const cleanBase = cleanValue(baseUrl).replace(/\/+$/, '');
  const explicitUrl = cleanValue(result.url || result.href);
  if (explicitUrl) {
    return cleanBase && explicitUrl.startsWith('/') && !explicitUrl.startsWith('//') ? `${cleanBase}${explicitUrl}` : explicitUrl;
  }

  const path = getAgentResultPath(result);
  if (!path) return '';

  return cleanBase ? `${cleanBase}${path}` : path;
};

export const normalizeAgentResult = (rawResult = {}) => {
  const source = rawResult.data && !rawResult.kind && !rawResult.type && !rawResult.component ? rawResult.data : rawResult;
  const kind = normalizeAgentResultKind(source.kind || source.type || source.component || source.resourceType || source.resource_type);
  const id = cleanValue(firstValue(source, ['id', 'resourceId', 'data.id']));
  const title = cleanValue(firstValue(source, ['title', 'name', 'label', 'data.title', 'data.name', 'data.label'])) || 'Untitled';
  const subtitle = cleanValue(firstValue(source, ['subtitle', 'description', 'realm.name', 'location', 'data.subtitle', 'data.description']));
  const status = cleanValue(firstValue(source, ['status', 'state', 'data.status', 'data.state']));
  const path = getAgentResultPath(source);
  const url = getAgentResultUrl(source);
  const metadata = firstValue(source, ['metadata', 'meta', 'data.metadata', 'data.meta']) || {};

  if (!kind || !AGENT_RESULT_KINDS.includes(kind)) return null;

  return {
    ...source,
    id,
    kind,
    title,
    subtitle,
    status,
    path,
    url,
    metadata: typeof metadata === 'object' && !Array.isArray(metadata) ? metadata : {},
  };
};

export const normalizeAgentResults = (results) => {
  if (!results) return [];
  const values = Array.isArray(results) ? results : [results];
  return values.map(normalizeAgentResult).filter(Boolean);
};

const escapeMarkdown = (value) => cleanValue(value).replace(/([\\[\]])/g, '\\$1');

export const agentResultsToMarkdown = (results, baseUrl = '') =>
  normalizeAgentResults(results)
    .map((result) => {
      const url = getAgentResultUrl(result, baseUrl);
      return url ? `- [${escapeMarkdown(result.title)}](${url})` : `- ${escapeMarkdown(result.title)}`;
    })
    .join('\n');

const escapeHtml = (value) =>
  cleanValue(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

export const agentResultsToHtml = (results, baseUrl = '') =>
  normalizeAgentResults(results)
    .map((result) => {
      const url = getAgentResultUrl(result, baseUrl);
      const label = escapeHtml(result.title);
      const meta = [result.subtitle, result.status].filter(Boolean).map(escapeHtml).join(' - ');
      const body = meta ? `${label}<br><small>${meta}</small>` : label;

      return url ? `<a href="${escapeHtml(url)}">${body}</a>` : `<span>${body}</span>`;
    })
    .join('');
